import * as React from 'react';
import { Button, SxProps, Theme } from '@mui/material';

interface IconButtonSxOptions {
  color?: string;
  fontSize?: string;
  isIconOnly?: boolean;
}

export const iconButtonSx = ({
  color = 'var(--color-primary)',
  fontSize = 'var(--text-md)',
  isIconOnly = false,
}: IconButtonSxOptions = {}) => ({
  color,
  fontWeight: 700,
  fontSize,
  backgroundColor: 'transparent',
  // Square, centered hit area so the hover state is a clean shape
  // rather than a cramped rectangle around the bare icon.
  minWidth: isIconOnly ? 'unset' : undefined,
  padding: isIconOnly ? 'var(--space-2)' : 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  lineHeight: 1,
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: isIconOnly ? 0 : 'var(--space-2)',
  },
  // Tint the hover background from the icon's own color so it always
  // matches (e.g. a red delete icon gets a soft red hover, not blue).
  // Keep `color` on hover too, otherwise the theme's text-button hover
  // forces it to primary (blue).
  '&:hover': {
    color,
    backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
  },
});

interface IconButtonProps {
  icon: React.ReactNode;
  label?: string;
  /** Accessible name — required when the button renders icon-only. */
  ariaLabel?: string;
  color?: string;
  onClick?: () => void;
  fontSize?: string;
  sx?: SxProps<Theme>;
}

export const IconButton: React.FC<IconButtonProps> = ({
  label,
  ariaLabel,
  icon,
  onClick,
  fontSize,
  color,
  sx,
}) => {
  return (
    <Button
      startIcon={icon}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      sx={{ ...iconButtonSx({ color, fontSize, isIconOnly: !label }), ...sx }}
    >
      {label}
    </Button>
  );
};
